import type { Campaign, ItemScopedDealEconomics, ScopedItemEconomics, StoreResponse } from '@clarity/contract';
import { useDealEconomics } from '../../queries/index.js';
import { isNotYetOnboarded } from '../../lib/lifecycle.js';
import { dec, isOnboardingRequired, money, pct as fmtPct, signed } from '../format.js';

/**
 * DealEconomicsPanel (clarity-campaign-terms-economics task 6.3, Req 6.2 / 6.4),
 * rebuilt from the agreed mock `brand/campaigns-terms-mock.html` (VIEW ·
 * CAMPAIGN DETAIL — item-scoped Lqta, the `#v-lqta` "What this deal actually
 * cost you" panel) in the teal-glass world. The mock is the contract.
 *
 * A "what this deal actually cost you" panel for an ITEM-SCOPED campaign: the
 * real cost of the headline discount measured against the FOOD COST of exactly
 * the scoped meals (from the catalog), not the headline percentage. It is a pure
 * READ of the server-computed {@link ItemScopedDealEconomics} — reporting only,
 * never a match gate (Req 4.4).
 *
 * Mounted by {@link CampaignDetail} as a sibling `.card.panel` (like
 * {@link CpcBreakeven}) ONLY for `campaign.itemScoped === true`; a non-scoped
 * campaign never mounts it. It draws:
 *  - the per-scoped-item table — meal, base price, food cost % (coloured
 *    hi/mid/lo by the mock's `fcClass`: ≥40 danger, ≥33 warn, else ok), food
 *    cost, discount given (with a ⤓cap marker when the cap bound it),
 *    margin/order (sign-coloured, with an explicit +/− so the sign is carried by
 *    text, not colour alone — WCAG 1.4.1);
 *  - the avg summary row (avg food cost / avg discount / avg margin + the "catalog
 *    · per item" food-cost basis note);
 *  - a contrast callout — high food cost, not the headline percentage, is what
 *    makes a deal thin.
 *
 * The mock's "test discount" slider is OPTIONAL in v1 (tasks.md) — the panel
 * renders READ-ONLY at the deal's declared `pct`; the server owns the margin
 * computation (reusing the core food-cost rule), so a client-side what-if would
 * duplicate that logic. Omitted until asked.
 *
 * `--font-num` on every numeric cell; logical properties / the shared `.data`
 * table (RTL-ready); the loading / empty / error states mirror the other
 * campaign components.
 */
export function DealEconomicsPanel({
  store,
  campaign,
  currency,
}: {
  store: StoreResponse;
  campaign: Campaign;
  currency: string;
}) {
  const enabled = !isNotYetOnboarded(store);
  const query = useDealEconomics(store.id, campaign.id, undefined, undefined, enabled);
  const onboardingBlocked = isOnboardingRequired(query.error);

  if (query.isLoading) return <EconomicsLoading />;
  // A `409` is the expected not-yet-onboarded block — fall through to the empty
  // state rather than an error (same contract as the other campaign reads).
  if (query.isError && !onboardingBlocked) return <EconomicsError />;

  const data: ItemScopedDealEconomics | null =
    query.data?.status === 200 ? query.data.body : null;

  if (!data) return <EconomicsError />;

  return <EconomicsLoaded data={data} currency={currency} />;
}

// ── The loaded panel ────────────────────────────────────────────────────────

function EconomicsLoaded({
  data,
  currency,
}: {
  data: ItemScopedDealEconomics;
  currency: string;
}) {
  const headlinePct = Math.round(data.pct * 100);

  return (
    <section className="card panel me" aria-label="Deal economics — food cost">
      <div className="me__head">
        <h3 className="me__title">What this deal actually cost you</h3>
        <p className="pm__hint">
          {' '}
          · {data.label} is {headlinePct}% off {data.items.length === 1 ? 'one' : data.items.length}{' '}
          specific meal{data.items.length === 1 ? '' : 's'}. The headline percentage is what the
          customer saw. The real cost is the discount measured against the <b>food cost</b> of exactly
          those items (from your catalog) — a {headlinePct}% deal on a 42%-food-cost meal is a very
          different event from {headlinePct}% on a 30%-food-cost one.
        </p>
      </div>

      {data.items.length === 0 ? (
        <EconomicsEmpty />
      ) : (
        <>
          <table className="data me-grid" aria-label="Per-scoped-item food-cost margin">
            <thead>
              <tr>
                <th scope="col">Scoped meal</th>
                <th scope="col" className="num-col">
                  Base price
                </th>
                <th scope="col" className="num-col">
                  Food cost %
                </th>
                <th scope="col" className="num-col">
                  Food cost
                </th>
                <th scope="col" className="num-col">
                  Discount given
                </th>
                <th scope="col" className="num-col">
                  Margin / order
                </th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item, i) => (
                <ItemRow key={`${item.name}-${i}`} item={item} />
              ))}
            </tbody>
          </table>

          <SummaryRow data={data} currency={currency} />
          <Callout data={data} headlinePct={headlinePct} currency={currency} />
        </>
      )}
    </section>
  );
}

/** One scoped-item row — all figures on `--font-num` (via `.num-col`). The
 *  food-cost % is coloured by band; the discount carries a ⤓cap marker when the
 *  cap bound it; the margin carries an explicit sign (text, not colour alone). */
function ItemRow({ item }: { item: ScopedItemEconomics }) {
  const positive = item.marginPerOrder >= 0;
  return (
    <tr>
      <td>
        <div className="mi">
          <span className="nm">{item.name}</span>
        </div>
      </td>
      <td className="num-col">{dec(item.basePrice)}</td>
      <td className={`num-col fc ${fcClass(item.foodCostPct)}`}>{fmtPct(item.foodCostPct)}</td>
      <td className="num-col">{dec(item.foodCost)}</td>
      <td className="num-col">
        {dec(item.discountGiven)}
        {item.capped && (
          <span className="me-cap">
            {' '}
            <span aria-hidden="true">⤓</span>cap
          </span>
        )}
      </td>
      <td className={`num-col mg ${positive ? 'pos' : 'neg'}`}>{signed(item.marginPerOrder)}</td>
    </tr>
  );
}

/** The averaged summary row + the food-cost-basis note. Avg margin is
 *  sign-coloured AND sign-prefixed (WCAG 1.4.1). */
function SummaryRow({ data, currency }: { data: ItemScopedDealEconomics; currency: string }) {
  const { summary } = data;
  const positive = summary.avgMargin >= 0;
  return (
    <div className="me-foot">
      <div className="me-kpi">
        <span className="k">Avg food cost</span>
        <span className="v">{money(summary.avgFoodCost, currency)}</span>
      </div>
      <div className="me-kpi">
        <span className="k">Avg discount given</span>
        <span className="v">{money(summary.avgDiscount, currency)}</span>
      </div>
      <div className="me-kpi">
        <span className="k">Avg margin / order</span>
        <span className={`v ${positive ? 'pos' : 'neg'}`}>
          {signed(summary.avgMargin)} {currency}
        </span>
      </div>
      <div className="me-kpi">
        <span className="k">Food cost basis</span>
        <span className="v me-kpi__note">catalog · per item</span>
      </div>
    </div>
  );
}

/**
 * The contrast callout (static copy, faithful to the mock's intent): high food
 * cost, not the headline percentage, is what makes the deal thin. The average
 * food-cost % is computed from the items (`avgFoodCost / avgBasePrice`).
 */
function Callout({
  data,
  headlinePct,
  currency,
}: {
  data: ItemScopedDealEconomics;
  headlinePct: number;
  currency: string;
}) {
  const avgFoodPct = avgFoodCostPct(data);
  const avgMargin = data.summary.avgMargin;
  const thin = avgMargin < 0.6;
  return (
    <div className="me-callout warn" role="note">
      {thin ? (
        <>
          At <b>{headlinePct}% off</b>, these meals carry a{' '}
          <b>{avgFoodPct !== null ? `${fmtPct(avgFoodPct)}` : 'high'} food cost</b> — after food cost
          and commission the margin is only{' '}
          <b>
            {signed(avgMargin)} {currency}/order
          </b>
          . High food cost is what makes this deal thin — not the headline percentage.
        </>
      ) : (
        <>
          At <b>{headlinePct}% off</b> the margin holds at{' '}
          <b>
            {signed(avgMargin)} {currency}/order
          </b>
          . Food cost{avgFoodPct !== null ? ` (${fmtPct(avgFoodPct)})` : ''} is the dominant cost;
          watch it if you deepen the discount.
        </>
      )}
    </div>
  );
}

// ── Empty / loading / error states (mirroring the other campaign components) ──

/** The scoped-but-unpriced empty state: the campaign is item-scoped but no
 *  scoped item resolved a catalog price, so there are no rows to show. */
function EconomicsEmpty() {
  return (
    <div className="empty" role="status">
      <p className="empty__sub muted">
        No scoped items priced — none of this deal’s scoped meals resolved a catalog price in range.
      </p>
    </div>
  );
}

function EconomicsLoading() {
  return (
    <section className="card panel me" aria-label="Deal economics — food cost">
      <div className="card campaigns__loading" role="status" aria-busy="true" aria-live="polite">
        <span className="sr-only">Computing the deal’s food-cost margin…</span>
        <span className="sk" style={{ inlineSize: '55%' }} aria-hidden="true" />
        <span className="sk" style={{ inlineSize: '85%' }} aria-hidden="true" />
        <span className="sk" style={{ inlineSize: '70%' }} aria-hidden="true" />
      </div>
    </section>
  );
}

function EconomicsError() {
  return (
    <section className="card panel me" aria-label="Deal economics — food cost">
      <p className="error" role="alert">
        Could not load this deal’s food-cost economics.
      </p>
    </section>
  );
}

// ── Pure helpers ──────────────────────────────────────────────────────────────

/** The mock's `fcClass`: ≥40 danger (hi), ≥33 warn (mid), else ok (lo). */
function fcClass(foodCostPct: number): 'hi' | 'mid' | 'lo' {
  if (foodCostPct >= 40) return 'hi';
  if (foodCostPct >= 33) return 'mid';
  return 'lo';
}

/**
 * The average food-cost PERCENTAGE across the scoped items, derived from the
 * averaged amounts (`avgFoodCost / avgBasePrice × 100`). Returns null when there
 * is no base to divide by, so the callout never shows a fabricated percentage.
 */
function avgFoodCostPct(data: ItemScopedDealEconomics): number | null {
  if (data.items.length === 0) return null;
  const avgBase =
    data.items.reduce((sum, it) => sum + it.basePrice, 0) / data.items.length;
  if (avgBase <= 0) return null;
  return Math.round((data.summary.avgFoodCost / avgBase) * 100);
}
